<div
  class="modal-overlay"
  [class.android-platform]="isAndroid"
  *ngIf="isOpen"
  (click)="dismiss()"
>
  <div class="modal-content" (click)="$event.stopPropagation()">
    <!-- Fixed close button in top corner of content -->
    <button
      (click)="dismiss()"
      class="close-button-fixed"
      type="button"
      aria-label="Close"
    >
      <ion-icon name="close"></ion-icon>
      <span class="close-text">X</span>
    </button>
    <!-- Mobile Header - always visible -->
    <div class="modal-header">
      <h2 class="modal-title">
        {{ translations.continuousConversation || "Continuous Conversation" }}
      </h2>
    </div>

    <ion-content>
      <div class="container">
        <!-- Recording Controls -->
        <ion-card class="control-card">
          <ion-card-content>
            <div class="recording-status">
              <ion-icon
                [name]="recording ? 'mic' : 'mic-outline'"
                [class.recording]="recording"
                class="mic-icon"
              ></ion-icon>
              <p class="status-text">
                {{ statusText }}
              </p>
              <p class="language-info">
                {{ getLanguageName(selectedSourceLanguage) }} →
                {{ getLanguageName(selectedTargetLanguage) }}
              </p>
              <p *ngIf="recording && usingLive" class="live-badge">
                {{ translations.liveTranslation || "Live translation" }}
              </p>
              <p *ngIf="liveNotice" class="live-notice">{{ liveNotice }}</p>
              <p *ngIf="liveCaption" class="live-caption">{{ liveCaption }}</p>
            </div>

            <div class="button-group">
              <ion-button
                expand="block"
                [color]="clickedButton === 'start' ? 'primary' : 'success'"
                size="large"
                (click)="startRecording()"
                [disabled]="recording || clickedButton === 'start'"
                [class.clicked]="clickedButton === 'start'"
                class="action-button start-button"
              >
                <ion-icon
                  slot="start"
                  name="play"
                  aria-hidden="true"
                ></ion-icon>
                <span>{{
                  translations.startTranscribing || "Start Transcribing"
                }}</span>
              </ion-button>
              <ion-button
                expand="block"
                [color]="clickedButton === 'stop' ? 'dark' : 'danger'"
                size="large"
                (click)="stopRecording()"
                [disabled]="!recording || clickedButton === 'stop'"
                [class.clicked]="clickedButton === 'stop'"
                class="action-button stop-button"
              >
                <ion-icon
                  slot="start"
                  name="square"
                  aria-hidden="true"
                ></ion-icon>
                <span>{{
                  translations.stopTranscribing || "Stop Transcribing"
                }}</span>
              </ion-button>
              <ion-button
                expand="block"
                [fill]="clickedButton === 'clear' ? 'solid' : 'outline'"
                [color]="clickedButton === 'clear' ? 'medium' : undefined"
                size="large"
                (click)="clearTranscripts()"
                [disabled]="
                  recording ||
                  transcripts.length === 0 ||
                  clickedButton === 'clear'
                "
                [class.clicked]="clickedButton === 'clear'"
                class="action-button clear-button"
              >
                <ion-icon
                  slot="start"
                  name="trash-outline"
                  aria-hidden="true"
                ></ion-icon>
                <span>{{ translations.clear || "Clear" }}</span>
              </ion-button>

              <!-- Audio Toggle Button -->
              <!-- For iOS browser: Allow clicking even when recording so user can start playback -->
              <ion-button
                expand="block"
                [fill]="audioEnabled() ? 'solid' : 'outline'"
                [color]="audioEnabled() ? 'primary' : 'medium'"
                size="large"
                (click)="toggleAudio()"
                [disabled]="false"
                class="action-button audio-button"
              >
                <ion-icon
                  slot="start"
                  name="volume-high"
                  aria-hidden="true"
                ></ion-icon>
                <span>{{
                  audioEnabled()
                    ? translations.audioOn || "Audio: ON"
                    : translations.audioOff || "Audio: OFF"
                }}</span>
                <ion-icon
                  *ngIf="!audioEnabled()"
                  slot="end"
                  name="close-circle"
                  style="font-size: 1.2em; margin-left: 8px"
                  aria-hidden="true"
                ></ion-icon>
              </ion-button>
            </div>

            <!-- Lecture Mode Checkbox -->
            <div
              class="lecture-mode-section mt-3"
              *ngIf="!isLiveTranslationEnabled()"
            >
              <ion-item lines="none" class="lecture-mode-item">
                <ion-checkbox
                  [(ngModel)]="lectureMode"
                  [disabled]="recording"
                  slot="start"
                ></ion-checkbox>
                <ion-label class="lecture-mode-label">
                  <div class="lecture-mode-header">
                    <h3>{{ translations.lectureMode || "Lecture Mode" }}</h3>
                    <button
                      type="button"
                      class="info-icon"
                      (click)="showLectureModeInfo($event)"
                      (touchstart)="showLectureModeInfo($event)"
                      [disabled]="recording"
                      [attr.aria-label]="translations.lectureModeDescription || 'Lecture mode information'"
                    >
                      !
                    </button>
                  </div>
                </ion-label>
              </ion-item>
            </div>

            <!-- Advanced Model Checkbox -->
            <div
              class="lecture-mode-section mt-2"
              *ngIf="!isLiveTranslationEnabled()"
            >
              <ion-item lines="none" class="lecture-mode-item">
                <ion-checkbox
                  [ngModel]="isAdvancedModelEnabled()"
                  (ngModelChange)="onAdvancedModelChange($event)"
                  [disabled]="recording || isGlobalAdvancedModelEnabled()"
                  slot="start"
                ></ion-checkbox>
                <ion-label class="lecture-mode-label">
                  <div class="lecture-mode-header">
                    <h3>
                      {{
                        translations.advancedModel ||
                          "Advanced translation model"
                      }}
                    </h3>
                    <button
                      type="button"
                      class="info-icon"
                      (click)="showAdvancedModelInfo($event)"
                      (touchstart)="showAdvancedModelInfo($event)"
                      [attr.aria-label]="translations.advancedModelDescription || 'Advanced translation model information'"
                    >
                      !
                    </button>
                  </div>
                </ion-label>
              </ion-item>
            </div>

            <!-- Word Limit Error - Prominent Display (like manual recording) -->
            <div
              *ngIf="errorMessage && isWordLimitError(errorMessage)"
              class="mt-3 p-4 bg-red-50 border-2 border-red-500 rounded-lg"
            >
              <div class="flex items-start gap-3">
                <span class="text-2xl">⚠️</span>
                <div class="flex-1">
                  <h3 class="text-red-800 font-bold text-lg mb-2">
                    {{ translations.wordLimitExceededTitle }}
                  </h3>
                  <p class="text-red-700 text-base mb-3">
                    {{ errorMessage }}
                  </p>
                  <div class="flex gap-2">
                    <button
                      (click)="onBuyWords()"
                      class="px-4 py-2 bg-red-600 text-white rounded-md font-semibold hover:bg-red-700"
                    >
                      {{ translations.buyMoreWords }}
                    </button>
                    <button
                      (click)="dismissError()"
                      class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md font-semibold hover:bg-gray-400"
                    >
                      {{ translations.dismiss }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Timeout Error - Prominent Display -->
            <div
              *ngIf="errorMessage && isTimeoutError(errorMessage)"
              class="mt-3 p-4 bg-orange-50 border-2 border-orange-500 rounded-lg"
            >
              <div class="flex items-start gap-3">
                <span class="text-2xl">⏱️</span>
                <div class="flex-1">
                  <h3 class="text-orange-800 font-bold text-lg mb-2">
                    {{ translations.transcriptionTimeoutTitle }}
                  </h3>
                  <p class="text-orange-700 text-base">
                    {{ errorMessage }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Other Errors - Standard Display -->
            <div
              *ngIf="
                errorMessage &&
                !isWordLimitError(errorMessage) &&
                !isTimeoutError(errorMessage)
              "
              class="mt-3"
            >
              <p class="error-message">{{ errorMessage }}</p>
            </div>
          </ion-card-content>
        </ion-card>

        <!-- Transcripts -->
        <ion-card class="transcripts-card" *ngIf="transcripts.length > 0">
          <ion-card-header>
            <ion-card-title>
              <ion-icon name="document-text" class="card-icon"></ion-icon>
              <span
                >{{ translations.transcripts || "Transcripts" }} ({{
                  transcripts.length
                }})</span
              >
            </ion-card-title>
          </ion-card-header>
          <ion-card-content>
            <div class="transcript-list">
              <div
                *ngFor="let transcript of transcripts; trackBy: trackTranscript"
                class="transcript-item"
              >
                <div class="transcript-header">
                  <ion-badge color="primary"
                    >{{ translations.conversation || "Conversation" }} #{{
                      transcript.chunkIndex + 1
                    }}</ion-badge
                  >
                  <span class="timestamp">{{
                    formatTime(transcript.timestamp)
                  }}</span>
                </div>
                <p class="transcript-text">{{ transcript.text }}</p>
              </div>
            </div>
          </ion-card-content>
        </ion-card>

        <!-- Empty State -->
        <ion-card
          *ngIf="transcripts.length === 0 && !recording"
          class="empty-card"
        >
          <ion-card-content>
            <div class="empty-state">
              <ion-icon name="mic-outline" class="empty-icon"></ion-icon>
              <p>
                {{
                  translations.noTranscriptsYet ||
                    "No transcripts yet. Start transcribing to begin!"
                }}
              </p>
            </div>
          </ion-card-content>
        </ion-card>
      </div>
    </ion-content>
  </div>
</div>
